import React, { useState } from 'react';
import { ThumbsUp, ThumbsDown, Check, Loader } from 'lucide-react';
import { correctionsApi } from '../api/corrections';
import { Entity } from '../types';
import { useToast } from './ui/Toast';
import { errorMessage } from '../utils/errors';

interface FeedbackButtonsProps {
  entity: Entity;
  onFeedbackSubmitted?: () => void;
  onNegativeFeedback?: () => void;  // Trigger edit mode for thumbs down
}

/**
 * FeedbackButtons - Simple thumbs up/down feedback for every entity
 *
 * Thumbs Up (👍): Confirms the extraction is correct
 * Thumbs Down (👎): Opens correction modal to fix the value
 *
 * All feedback feeds into the learning system!
 */
const FeedbackButtons: React.FC<FeedbackButtonsProps> = ({
  entity,
  onFeedbackSubmitted,
  onNegativeFeedback
}) => {
  const toast = useToast();
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [feedbackType, setFeedbackType] = useState<'positive' | 'negative' | null>(null);

  const handlePositiveFeedback = async () => {
    try {
      setSubmitting(true);
      setFeedbackType('positive');

      // Submit positive confirmation to learning system
      // This confirms the current value is correct and helps the system learn patterns
      await correctionsApi.submitHybridCorrection({
        entity_id: entity.id,
        corrected_value: entity.text, // Confirm current value is correct
        field_type: entity.label,
        is_confirmation: true  // Mark as confirmation (thumbs up)
      });

      setSuccess(true);

      // Reset after 2 seconds
      setTimeout(() => {
        setSuccess(false);
        setFeedbackType(null);
      }, 2000);

      onFeedbackSubmitted?.();
    } catch (error) {
      // The button reset itself silently, so a failed submit was
      // indistinguishable from one that worked.
      toast.error(errorMessage(error, 'Couldn’t record your feedback.'));
      setFeedbackType(null);
    } finally {
      setSubmitting(false);
    }
  };

  const handleNegativeFeedback = () => {
    // Trigger edit mode in parent component (EntityCorrectionWidget)
    // This allows the user to correct the value
    setFeedbackType('negative');
    onNegativeFeedback?.();
  };

  // Show success state
  if (success && feedbackType === 'positive') {
    return (
      <div className="flex items-center gap-1 text-green-400 text-xs animate-in fade-in">
        <Check className="w-3 h-3" />
        <span>Thanks!</span>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1">
      {/* Thumbs Up - Positive Feedback */}
      <button
        onClick={handlePositiveFeedback}
        disabled={submitting}
        className="p-1 text-green-400/70 hover:text-green-300 hover:bg-green-500/20 rounded transition-colors disabled:opacity-50 disabled:cursor-wait"
        title="Correct ✓"
        aria-label="Mark as correct"
      >
        {submitting && feedbackType === 'positive' ? (
          <Loader className="w-3.5 h-3.5 animate-spin" />
        ) : (
          <ThumbsUp className="w-3.5 h-3.5" />
        )}
      </button>

      {/* Thumbs Down - Negative Feedback */}
      <button
        onClick={handleNegativeFeedback}
        disabled={submitting}
        className="p-1 text-red-400/70 hover:text-red-300 hover:bg-red-500/20 rounded transition-colors disabled:opacity-50"
        title="Incorrect - Fix it"
        aria-label="Mark as incorrect and fix"
      >
        <ThumbsDown className="w-3.5 h-3.5" />
      </button>
    </div>
  );
};

export default FeedbackButtons;
